const internalPresignedHosts = new Set([
  "minio",
  "storage-api",
  "void-minio",
  "void-storage-api",
]);

export type FileAccessPurpose = "preview" | "inline" | "attachment";
export type FileAccessMediaKind = "image" | "video" | "audio" | "document";

export interface FileReferenceMetadata {
  id?: string | null;
  fileId?: string | null;
  storageKey?: string | null;
  url?: string | null;
  thumbnailKey?: string | null;
  thumbnailUrl?: string | null;
}

const firstNonEmpty = (...values: Array<string | null | undefined>) =>
  values.find((value) => Boolean(value?.trim()))?.trim();

const uniqueNonEmpty = (values: Array<string | null | undefined>) => {
  const unique = new Set<string>();

  values.forEach((value) => {
    const trimmed = value?.trim();
    if (trimmed) unique.add(trimmed);
  });

  return Array.from(unique);
};

export const getFileAccessReferences = (
  fileMetadata: FileReferenceMetadata | null | undefined,
  purpose: FileAccessPurpose,
  mediaKind: FileAccessMediaKind
): string[] => {
  if (!fileMetadata) {
    return [];
  }

  const originalReference = firstNonEmpty(fileMetadata.storageKey, fileMetadata.url);

  if (purpose === "attachment") {
    return uniqueNonEmpty([originalReference, fileMetadata.fileId]);
  }

  if (purpose === "preview") {
    const thumbnailReferences = [fileMetadata.thumbnailKey, fileMetadata.thumbnailUrl];
    if (mediaKind === "video") {
      return uniqueNonEmpty(thumbnailReferences);
    }

    return uniqueNonEmpty([...thumbnailReferences, fileMetadata.fileId, originalReference]);
  }

  return uniqueNonEmpty([fileMetadata.fileId, originalReference]);
};

export const getFileAccessReference = (
  fileMetadata: FileReferenceMetadata | null | undefined,
  purpose: FileAccessPurpose,
  mediaKind: FileAccessMediaKind
) => getFileAccessReferences(fileMetadata, purpose, mediaKind)[0];

export const isBrowserReachableFileURL = (value: string): boolean => {
  try {
    const url = new URL(value);
    return !internalPresignedHosts.has(url.hostname.toLowerCase());
  } catch {
    return false;
  }
};
